/**
 * Theme definitions for VO Search component.
 *
 * Theme Categories:
 * - vo-auto/vo-light/vo-dark: Current themes (labeled Auto, Light, Dark), the
 *   default for new instances, with VO styling and glass morphism
 * - auto/light/dark: Classic themes (labeled Classic Auto/Light/Dark) that BLEND
 *   IN with any customer site; existing instances keep these
 *
 * The user's Ghost CMS primaryColor always takes precedence over theme defaults.
 */

/**
 * Base theme structure - all themes extend this.
 * Null values must be overridden by each theme.
 */
const baseTheme = {
  // Primary color - sourced from user's Ghost CMS theme/brand color (Index.PrimaryColor)
  // Theme value is FALLBACK only, user's Ghost primaryColor always takes precedence
  primaryColor: null,
  paginationButtonBgColor: null, // Defaults to primaryColor if not set

  // Core UI colors
  backgroundColor: null,
  textColor: null,
  textColorSecondary: null,
  inputBackgroundColor: null,
  inputTextColor: null,
  inputIconColor: null,
  inputPlaceholderColor: null,

  // Input scale (item #16445). Null in every theme on purpose: the value that
  // renders today lives in the CSS fallbacks -- var(--vo-search-input-font-size,
  // 18px) and var(--vo-search-input-height, auto) -- and a theme repeating those
  // numbers would be a second default free to drift from the first. The keys
  // exist so a theme CAN carry a scale where one is wanted.
  inputFontSize: null,
  inputHeight: null,

  borderColor: null,
  paginationIconColor: null,
  modalTriggerIconColor: null,

  // Feature icons
  featuredIconColor: null,
  lockedIconColor: null,
  membersIconColor: null,

  // Feedback colors
  errorTextColor: null,
  errorBgColor: null,
  noResultsColor: null,

  // Modal/Overlay
  backdropColor: null,
  backdropBlur: null,

  // Shadows
  shadowSm: null,
  shadowMd: null,
  shadowLg: null,

  // Glass morphism (for vo-dark theme)
  surfaceColor: null,
  surfaceBorderColor: null,

  // Result styling
  resultHoverBg: null,
  resultActiveBg: null,
  highlightColor: null,

  // Tag styling
  tagBgColor: null,
  tagBorderColor: null,
  tagTextColor: null,
};

/**
 * Available themes.
 *
 * CLASSIC THEMES (light/dark):
 * - Subtle, neutral colors that blend with any customer site
 * - No VO branding - customer's primaryColor drives accents
 * - Minimal visual footprint
 *
 * CURRENT THEMES (vo-light/vo-dark):
 * - Distinctive VO branding with purple accents
 * - Glass morphism effects on vo-dark
 * - The default for new instances (vo-auto)
 */
export const themes = {
  /**
   * Classic Light theme - Subtle
   * Designed to BLEND IN with any light-colored website.
   */
  light: {
    ...baseTheme,
    // Primary - neutral fallback, customer's color takes over
    primaryColor: '#4b5563',
    paginationButtonBgColor: null,

    // Core UI - neutral grays that blend with any light site
    backgroundColor: '#ffffff',
    textColor: '#374151',
    textColorSecondary: '#6b7280',
    inputBackgroundColor: '#f9fafb',
    inputTextColor: '#374151',
    inputIconColor: '#9ca3af',
    inputPlaceholderColor: '#9ca3af',
    borderColor: '#e5e7eb',
    paginationIconColor: '#ffffff',
    modalTriggerIconColor: '#ffffff',

    // Feature icons - muted, professional
    featuredIconColor: '#d97706',
    lockedIconColor: '#6b7280',
    membersIconColor: '#6b7280',

    // Feedback colors
    errorTextColor: '#dc2626',
    errorBgColor: '#fef2f2',
    noResultsColor: '#6b7280',

    // Modal/Overlay - subtle
    backdropColor: 'rgba(0, 0, 0, 0.5)',
    backdropBlur: '4px',

    // Shadows - subtle
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.08)',

    // Surface
    surfaceColor: '#ffffff',
    surfaceBorderColor: '#e5e7eb',

    // Result styling - barely visible hover
    resultHoverBg: '#f9fafb',
    resultActiveBg: '#f3f4f6',
    highlightColor: null, // Uses primaryColor

    // Tags - subtle
    tagBgColor: 'rgba(0, 0, 0, 0.04)',
    tagBorderColor: 'rgba(0, 0, 0, 0.1)',
    tagTextColor: null, // Uses primaryColor
  },

  /**
   * Classic Dark theme - Subtle
   * Designed to BLEND IN with any dark-colored website.
   */
  dark: {
    ...baseTheme,
    // Primary - neutral fallback, customer's color takes over
    primaryColor: '#9ca3af',
    paginationButtonBgColor: null,

    // Core UI - neutral dark, blends with any dark site
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    textColor: '#e5e7eb',
    textColorSecondary: '#9ca3af',
    inputBackgroundColor: 'rgba(255, 255, 255, 0.06)',
    inputTextColor: '#e5e7eb',
    inputIconColor: 'rgba(255, 255, 255, 0.4)',
    inputPlaceholderColor: 'rgba(255, 255, 255, 0.4)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    paginationIconColor: '#ffffff',
    modalTriggerIconColor: '#ffffff',

    // Feature icons - muted for dark backgrounds
    featuredIconColor: '#fbbf24',
    lockedIconColor: '#9ca3af',
    membersIconColor: '#9ca3af',

    // Feedback colors - adjusted for dark backgrounds
    errorTextColor: '#f87171',
    errorBgColor: 'rgba(239, 68, 68, 0.15)',
    noResultsColor: 'rgba(255, 255, 255, 0.5)',

    // Modal/Overlay - subtle
    backdropColor: 'rgba(0, 0, 0, 0.6)',
    backdropBlur: '4px',

    // Shadows - more subtle on dark
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.2)',
    shadowMd: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
    shadowLg: '0 10px 15px -3px rgba(0, 0, 0, 0.4)',

    // Surface
    surfaceColor: 'rgba(40, 40, 40, 0.9)',
    surfaceBorderColor: 'rgba(255, 255, 255, 0.08)',

    // Result styling - barely visible hover
    resultHoverBg: 'rgba(255, 255, 255, 0.04)',
    resultActiveBg: 'rgba(255, 255, 255, 0.06)',
    highlightColor: null, // Uses primaryColor

    // Tags - subtle
    tagBgColor: 'rgba(255, 255, 255, 0.06)',
    tagBorderColor: 'rgba(255, 255, 255, 0.1)',
    tagTextColor: null, // Uses primaryColor
  },

  /**
   * Light theme (current) - Branded
   * Distinctive VO styling with purple accents.
   * Visually distinct from light theme with purple tints matching vo-dark.
   */
  'vo-light': {
    ...baseTheme,
    // VO purple branding
    primaryColor: '#712cf9',
    paginationButtonBgColor: null,

    // Core UI - light with purple tints (matches vo-dark styling)
    backgroundColor: 'rgba(250, 248, 255, 0.98)',
    textColor: '#1a1a2e',
    textColorSecondary: '#6d5a8f',
    inputBackgroundColor: 'rgba(113, 44, 249, 0.04)',
    inputTextColor: '#1a1a2e',
    inputIconColor: 'rgba(113, 44, 249, 0.5)',
    inputPlaceholderColor: 'rgba(113, 44, 249, 0.4)',
    borderColor: 'rgba(113, 44, 249, 0.2)',
    paginationIconColor: '#ffffff',
    modalTriggerIconColor: '#ffffff',

    // VO-branded icons - purple tinted
    featuredIconColor: '#f59e0b',
    lockedIconColor: '#712cf9',
    membersIconColor: '#712cf9',

    // Feedback colors
    errorTextColor: '#dc2626',
    errorBgColor: '#fef2f2',
    noResultsColor: '#6d5a8f',

    // Modal/Overlay - purple tinted
    backdropColor: 'rgba(20, 15, 40, 0.5)',
    backdropBlur: '12px',

    // Shadows - purple glow effect (light version)
    shadowSm: '0 1px 2px rgba(113, 44, 249, 0.06)',
    shadowMd: '0 4px 12px rgba(113, 44, 249, 0.1)',
    shadowLg: '0 15px 35px rgba(113, 44, 249, 0.12), 0 5px 15px rgba(0, 0, 0, 0.08)',

    // Surface - subtle purple tint
    surfaceColor: 'rgba(250, 248, 255, 0.95)',
    surfaceBorderColor: 'rgba(113, 44, 249, 0.2)',

    // Result styling - purple tinted hover
    resultHoverBg: 'rgba(113, 44, 249, 0.04)',
    resultActiveBg: 'rgba(113, 44, 249, 0.08)',
    highlightColor: '#712cf9',

    // Tags - VO purple with more presence
    tagBgColor: 'rgba(113, 44, 249, 0.1)',
    tagBorderColor: 'rgba(113, 44, 249, 0.25)',
    tagTextColor: '#712cf9',
  },

  /**
   * Dark theme (current) - Branded with Glass Morphism
   * Distinctive VO styling with glass morphism effects.
   * Visually distinct from dark theme with purple tints and glowing borders.
   */
  'vo-dark': {
    ...baseTheme,
    // VO purple branding (lighter for dark bg)
    primaryColor: '#8b4fff',
    paginationButtonBgColor: null,

    // Core UI - glass morphism with purple tint (more saturated than dark theme)
    backgroundColor: 'rgba(20, 15, 40, 0.97)',
    textColor: '#ffffff',
    textColorSecondary: '#c4b5fd',
    inputBackgroundColor: 'rgba(139, 79, 255, 0.1)',
    inputTextColor: '#ffffff',
    inputIconColor: 'rgba(167, 139, 250, 0.7)',
    inputPlaceholderColor: 'rgba(196, 181, 253, 0.5)',
    borderColor: 'rgba(139, 79, 255, 0.35)',
    paginationIconColor: '#ffffff',
    modalTriggerIconColor: '#ffffff',

    // VO-branded icons - purple tinted
    featuredIconColor: '#fbbf24',
    lockedIconColor: '#a78bfa',
    membersIconColor: '#a78bfa',

    // Feedback colors
    errorTextColor: '#f87171',
    errorBgColor: 'rgba(239, 68, 68, 0.15)',
    noResultsColor: 'rgba(196, 181, 253, 0.6)',

    // Modal/Overlay - glass morphism with purple tint
    backdropColor: 'rgba(20, 10, 40, 0.85)',
    backdropBlur: '16px',

    // Shadows - purple glow effect
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 4px 20px rgba(139, 79, 255, 0.15)',
    shadowLg: '0 20px 40px rgba(139, 79, 255, 0.2), 0 10px 20px rgba(0, 0, 0, 0.4)',

    // Glass morphism surface - deeper purple
    surfaceColor: 'rgba(25, 18, 50, 0.92)',
    surfaceBorderColor: 'rgba(139, 79, 255, 0.35)',

    // Result styling - purple tinted hover
    resultHoverBg: 'rgba(139, 79, 255, 0.08)',
    resultActiveBg: 'rgba(139, 79, 255, 0.12)',
    highlightColor: '#a78bfa',

    // Tags - purple with more presence
    tagBgColor: 'rgba(139, 79, 255, 0.2)',
    tagBorderColor: 'rgba(139, 79, 255, 0.4)',
    tagTextColor: '#c4b5fd',
  },
};

/**
 * Detect user's system color scheme preference.
 *
 * @returns {'light' | 'dark'} The detected theme based on system preference
 */
export function getSystemTheme() {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/**
 * Resolve theme name, handling 'auto' and 'vo-auto' by detecting system preference.
 * Default is 'auto' (follows system preference with adaptive themes).
 *
 * @param {string} themeName - Theme name ('light', 'dark', 'vo-light', 'vo-dark', 'auto', 'vo-auto', or empty)
 * @returns {string} The resolved theme name
 */
export function resolveTheme(themeName) {
  if (!themeName || themeName === 'auto') {
    // Classic Auto - returns 'light' or 'dark'
    return getSystemTheme();
  }
  if (themeName === 'vo-auto') {
    // Current Auto - returns 'vo-light' or 'vo-dark'
    return getSystemTheme() === 'dark' ? 'vo-dark' : 'vo-light';
  }
  return themeName;
}

/**
 * Get theme defaults by name.
 * Falls back to 'light' for unknown themes.
 * Handles 'auto' by detecting system preference.
 *
 * @param {string} themeName - Theme name ('light', 'dark', 'vo-light', 'vo-dark', 'auto', or empty)
 * @returns {object} Theme configuration object
 */
export function getTheme(themeName) {
  const resolved = resolveTheme(themeName);
  return themes[resolved] || themes.light;
}

/**
 * List available theme names.
 *
 * @returns {string[]} Array of theme names
 */
export function getAvailableThemes() {
  return Object.keys(themes);
}

/**
 * Check if a theme exists.
 *
 * @param {string} themeName - Theme name to check
 * @returns {boolean} True if theme exists
 */
export function hasTheme(themeName) {
  return themeName in themes;
}

/**
 * Check if a theme is a VO branded theme.
 *
 * @param {string} themeName - Theme name to check
 * @returns {boolean} True if theme is VO branded
 */
export function isVOTheme(themeName) {
  return themeName === 'vo-light' || themeName === 'vo-dark' || themeName === 'vo-auto';
}

/**
 * Check if a theme uses glass morphism effects.
 *
 * @param {string} themeName - Theme name to check
 * @returns {boolean} True if theme uses glass morphism
 */
export function usesGlassMorphism(themeName) {
  return themeName === 'vo-dark';
}
